<script lang="ts">
	import type { WithElementRef } from 'bits-ui'
	import type { HTMLAttributes } from 'svelte/elements'
	import { cn } from 'tailwind-variants'

	import { useSidebar } from './context.svelte'

	let {
		ref = $bindable(null),
		side = 'left',
		variant = 'sidebar',
		collapsible = 'offcanvas',
		class: className,
		children,
		...restProps
	}: WithElementRef<HTMLAttributes<HTMLDivElement>> & {
		side?: 'left' | 'right'
		variant?: 'sidebar' | 'floating' | 'inset'
		collapsible?: 'offcanvas' | 'icon' | 'none'
	} = $props()

	const sidebar = useSidebar()
</script>

{#if collapsible === 'none'}
	<div
		bind:this={ref}
		class={cn(
			'bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col',
			className
		)}
		{...restProps}
	>
		{@render children?.()}
	</div>
{:else if !sidebar.isMobile}
	<div
		bind:this={ref}
		class="group peer text-sidebar-foreground hidden md:block"
		data-collapsible={sidebar.state === 'collapsed' ? collapsible : ''}
		data-side={side}
		data-slot="sidebar"
		data-state={sidebar.state}
		data-variant={variant}
	>
		<!-- This is what handles the sidebar gap on desktop -->
		<div
			class={cn(
				'relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear',
				'group-data-[collapsible=offcanvas]:w-0',
				'group-data-[side=right]:rotate-180',
				variant === 'floating' || variant === 'inset'
					? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
					: 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)'
			)}
			data-slot="sidebar-gap"
		></div>
		<div
			class={cn(
				'fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex',
				side === 'left'
					? 'inset-s-0 group-data-[collapsible=offcanvas]:-inset-s-(--sidebar-width)'
					: 'inset-e-0 group-data-[collapsible=offcanvas]:-inset-e-(--sidebar-width)',
				// Adjust the padding for floating and inset variants.
				variant === 'floating' || variant === 'inset'
					? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
					: 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-e group-data-[side=right]:border-s',
				className
			)}
			data-slot="sidebar-container"
			{...restProps}
		>
			<div
				class="bg-sidebar group-data-[variant=floating]:ring-sidebar-border flex size-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:shadow-sm group-data-[variant=floating]:ring-1"
				data-sidebar="sidebar"
				data-slot="sidebar-inner"
			>
				{@render children?.()}
			</div>
		</div>
	</div>
{/if}
